import { Link, Navigate, useParams } from "react-router-dom";
import { m as motion, useReducedMotion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CallToActionSection } from "@/components/call-to-action-section";
import { FunctionIllustration } from "@/components/member-showcase-illustration";
import { EXTERNAL_LINKS } from "@/lib/constants";
import { NEXT_COHORT_APPLY_CTA } from "@/lib/cohorts";
import { defaultViewport } from "@/lib/motion";
import { MEMBER_SHOWCASE_PATH } from "@/lib/routes";
import { breadcrumb } from "@/lib/seo-schemas";
import { useSEO } from "@/lib/use-seo";
import { getFunctionBySlug, getFunctionPath } from "@/lib/member-showcase";

const cardBorderColors = [
  "border-l-primary",
  "border-l-secondary",
  "border-l-accent",
] as const;

const illustrationHoverSpring = {
  type: "spring",
  stiffness: 380,
  damping: 14,
  mass: 0.7,
} as const;

const MemberShowcaseFunctionPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const fn = slug ? getFunctionBySlug(slug) : undefined;
  const path = fn ? getFunctionPath(fn.slug) : MEMBER_SHOWCASE_PATH;
  const shouldReduceMotion = useReducedMotion();

  useSEO({
    title: fn
      ? `${fn.title}: Member Showcase`
      : "Member Showcase",
    description:
      fn?.framing ??
      "See what HR and People teams are building with AI. Real shipped experiments, not theory.",
    path,
    ogImage: "/images/og-from-the-lab.jpg",
    jsonLd: fn
      ? breadcrumb([
          { name: "Home", path: "/" },
          { name: "Member Showcase", path: MEMBER_SHOWCASE_PATH },
          { name: fn.title, path },
        ])
      : undefined,
  });

  if (!fn) {
    return <Navigate to={MEMBER_SHOWCASE_PATH} replace />;
  }

  return (
    <div className="min-h-screen">
      <section className="relative overflow-hidden bg-gradient-mesh pt-20 md:pt-24 pb-10 md:pb-12">
        <div className="section-inner relative z-10 max-w-5xl mx-auto">
          <Link
            to={MEMBER_SHOWCASE_PATH}
            className="inline-block text-sm text-muted-foreground hover:text-primary transition-colors mb-4"
          >
            ← Back to Member Showcase
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            className="md:grid md:grid-cols-[1fr_auto] md:gap-10 md:items-center"
          >
            <div className="space-y-4">
              <h1 className="text-4xl md:text-5xl font-bold gradient-text">
                {fn.title}
              </h1>
              <p className="text-base md:text-lg text-foreground/80 leading-relaxed max-w-2xl">
                {fn.framing}
              </p>
            </div>
            <div className="mt-6 md:mt-0 flex justify-center md:justify-end shrink-0 overflow-visible p-4 md:p-6">
              <motion.div
                className="origin-center"
                whileHover={
                  shouldReduceMotion
                    ? undefined
                    : { scale: 1.12, rotate: 2.5 }
                }
                transition={illustrationHoverSpring}
              >
                <FunctionIllustration
                  slug={fn.slug}
                  className="w-full max-w-sm h-auto max-h-[160px] md:max-h-[150px] lg:max-h-[180px]"
                />
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="container-class relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-mesh opacity-10 pointer-events-none" />
        <div className="section-inner relative z-10 max-w-3xl mx-auto space-y-6">
          {fn.cards.map((card, index) => (
            <motion.article
              key={card.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={defaultViewport}
              transition={{ duration: 0.4, delay: index * 0.08 }}
              className={`glass-card p-6 md:p-8 border-l-4 ${cardBorderColors[index % cardBorderColors.length]} hover-lift`}
            >
              <p
                className="text-sm font-semibold tabular-nums tracking-wider text-primary mb-3"
                aria-hidden="true"
              >
                {String(index + 1).padStart(2, "0")}
              </p>
              <h2 className="text-2xl md:text-3xl font-bold mb-4 leading-tight">
                {card.title}
              </h2>
              <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
                {card.body}
              </p>
            </motion.article>
          ))}

          <motion.aside
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={defaultViewport}
            transition={{ duration: 0.45, delay: 0.1 }}
            className="glass-strong rounded-2xl p-6 md:p-8"
            aria-label="Next steps"
          >
            <h2 className="text-lg font-semibold mb-2">
              This is the kind of work you will learn to take on.
            </h2>
            <p className="text-foreground/85 leading-relaxed mb-5">
              The Mastermind is where you practice this on your own work. See the curriculum, or apply for the next cohort.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button asChild size="lg">
                <Link to="/curriculum">
                  See the curriculum
                  <ArrowRight className="h-4 w-4 ml-2" aria-hidden="true" />
                </Link>
              </Button>
              <Button variant="outline" asChild size="lg">
                <a href={EXTERNAL_LINKS.PREMIUM_MASTERMIND_APPLICATION}>
                  {NEXT_COHORT_APPLY_CTA}
                </a>
              </Button>
            </div>
          </motion.aside>
        </div>
      </section>

      <CallToActionSection />
    </div>
  );
};

export default MemberShowcaseFunctionPage;
